/* iMikel-SEPA-Mandate-Import: Excel + XML kombiniert */
import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Upload, FileSpreadsheet, FileCode, Loader2, CheckCircle2, AlertTriangle, ArrowRight, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { apiV1, tenantHeaders } from '@/lib/apiBase';
import { formatDeNumber } from '@/lib/utils';
import { useClientListMode } from '@/hooks/useClientListMode';
import ListModePager from '@/components/ListModePager';

const STATUS_META = {
  matched: { label: 'Match', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  multi: { label: 'Mehrdeutig', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  unmatched: { label: 'Kein Match', className: 'bg-red-100 text-red-800 border-red-200' },
  no_payer: { label: 'Kein Zahler', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  iban_unchanged: { label: 'Bereits aktuell', className: 'bg-blue-100 text-blue-800 border-blue-200' },
};

export default function ImikelImport({ onCompleted, tenantSlug = 'MA-GRE', tenantLabel = 'Musik Akademie Greven', allowedPrefixesText = 'TA.' }) {
  const excelRef = useRef(null);
  const xmlRef = useRef(null);
  const [excelFile, setExcelFile] = useState(null);
  const [xmlFiles, setXmlFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState('all');
  const [pickedCandidates, setPickedCandidates] = useState({}); // row_index -> guardian_id
  const [committing, setCommitting] = useState(false);

  const authHeaders = () => tenantHeaders();

  const runPreview = async () => {
    if (!excelFile || xmlFiles.length === 0) {
      toast.error('Bitte sowohl Excel als auch mindestens eine XML auswählen');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('excel', excelFile);
      xmlFiles.forEach(f => fd.append('xml_files', f));
      const res = await fetch(`${apiV1()}/imikel-import/sepa-mandates/preview`, {
        credentials: 'include',
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Preview fehlgeschlagen');
      }
      const data = await res.json();
      setPreview(data);
      setPickedCandidates({});
      toast.success(`${data.summary.matched} direkte Matches, ${data.summary.multi} zur Auswahl`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const itemsToCommit = () => {
    if (!preview) return [];
    const out = [];
    for (const item of preview.items) {
      if (item.status === 'matched') {
        out.push({
          guardian_id: item.matched_guardian_id,
          iban: item.xml_iban,
          bic: item.xml_bic,
          mandate_date: item.xml_mandate_date,
          account_holder: item.xml_name,
        });
      } else if (item.status === 'multi') {
        const picked = pickedCandidates[item.row_index];
        if (picked) {
          out.push({
            guardian_id: picked,
            iban: item.xml_iban,
            bic: item.xml_bic,
            mandate_date: item.xml_mandate_date,
            account_holder: item.xml_name,
          });
        }
      }
    }
    return out;
  };

  const runCommit = async () => {
    const items = itemsToCommit();
    if (items.length === 0) {
      toast.error('Keine Items zum Importieren ausgewählt');
      return;
    }
    if (!window.confirm(`${items.length} Mandate übernehmen? IBAN, BIC und Mandatsdatum werden in den Guardian-Records gespeichert.`)) return;
    setCommitting(true);
    try {
      const res = await fetch(`${apiV1()}/imikel-import/sepa-mandates/commit?tenant_slug=${encodeURIComponent(tenantSlug)}`, {
        credentials: 'include',
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Commit fehlgeschlagen');
      }
      const data = await res.json();
      toast.success(`${data.updated} Mandate aktualisiert${data.skipped ? `, ${data.skipped} übersprungen` : ''}`);
      setPreview(null);
      setExcelFile(null);
      setXmlFiles([]);
      setPickedCandidates({});
      if (excelRef.current) excelRef.current.value = '';
      if (xmlRef.current) xmlRef.current.value = '';
      onCompleted && onCompleted();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCommitting(false);
    }
  };

  const filteredItems = preview ? preview.items.filter(i => filter === 'all' || i.status === filter) : [];
  const {
    listMode, setListMode, pageSize, setPageSize, listPage, setListPage,
    totalCount, pageCount, visibleItems,
  } = useClientListMode(filteredItems, {
    resetKey: `${filter}|${preview?.items?.length || 0}`,
  });
  const willCommitCount = itemsToCommit().length;

  // Multi-Statistik: Wie viele Multi-Rows sind noch nicht gepickt?
  const multiItems = preview ? preview.items.filter(i => i.status === 'multi') : [];
  const multiPicked = multiItems.filter(i => pickedCandidates[i.row_index]).length;
  const multiAutoPickable = multiItems.filter(i =>
    !pickedCandidates[i.row_index]
    && i.candidates.filter(c => c.guardian_id && !c.has_iban).length === 1
  );

  const autoPickMultis = () => {
    const next = { ...pickedCandidates };
    let n = 0;
    for (const item of multiAutoPickable) {
      const eligible = item.candidates.find(c => c.guardian_id && !c.has_iban);
      if (eligible) {
        next[item.row_index] = eligible.guardian_id;
        n += 1;
      }
    }
    setPickedCandidates(next);
    if (n > 0) toast.success(`${n} Mehrdeutige automatisch gepickt (jeweils einziger eligible Kandidat ohne IBAN)`);
    else toast.info('Keine eindeutigen Auto-Picks gefunden — bitte manuell wählen.');
  };

  return (
    <div className="space-y-6" data-testid="imikel-import">
      <Card className="border-amber-200 bg-brand-red/5">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-brand-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Hinweis:</strong> Dieser Import läuft auf den Mandanten <strong>{tenantLabel}</strong> — unabhängig davon, welcher Mandant aktuell oben ausgewählt ist. Es werden ausschließlich <strong>{allowedPrefixesText}</strong>-Mandate verarbeitet.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" /> iMikel-SEPA-Mandate-Import
          </CardTitle>
          <CardDescription>
            Excel-Stammdaten + SEPA-XML hochladen → IBAN/BIC/Mandatsdatum für Familien füllen.
            Aktiv: <strong>{tenantLabel}</strong>; nur <strong>{allowedPrefixesText}</strong>-Mandate werden verarbeitet.
            Bei mehrdeutigen Treffern bitte Kandidat manuell wählen oder „Auto-Pick" verwenden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-brand-red/40 transition">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Excel <code className="text-xs bg-slate-100 px-1 rounded">konten.xlsx</code>
              </div>
              <input
                ref={excelRef}
                type="file"
                accept=".xlsx"
                onChange={e => setExcelFile(e.target.files?.[0] || null)}
                className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand-red/10 file:text-brand-red hover:file:bg-brand-red/10 cursor-pointer"
                data-testid="imikel-excel-input"
              />
              {excelFile && <p className="text-xs text-slate-500 mt-1">✓ {excelFile.name} ({Math.round(excelFile.size / 1024)} KB)</p>}
            </div>

            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-brand-red/40 transition">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                <FileCode className="w-4 h-4 text-blue-600" /> SEPA-XML(s) <span className="text-xs text-slate-500">(pain.008.001.02 — mehrere möglich)</span>
              </div>
              <input
                ref={xmlRef}
                type="file"
                accept=".xml"
                multiple
                onChange={e => setXmlFiles(Array.from(e.target.files || []))}
                className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand-red/10 file:text-brand-red hover:file:bg-brand-red/10 cursor-pointer"
                data-testid="imikel-xml-input"
              />
              {xmlFiles.length > 0 && <p className="text-xs text-slate-500 mt-1">✓ {xmlFiles.length} Datei(en) ausgewählt</p>}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={runPreview} disabled={!excelFile || xmlFiles.length === 0 || loading} data-testid="imikel-preview-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Vorschau erstellen
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardHeader>
            <CardTitle>Match-Vorschau</CardTitle>
            <CardDescription>
              {preview.unique_payers} unique Zahler aus {preview.total_xml_transactions} XML-Transaktionen · {preview.excel_rows} TA.-Konten in Excel
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-5 gap-3">
              {Object.entries(preview.summary).map(([key, count]) => {
                const meta = STATUS_META[key] || { label: key, className: 'bg-slate-100' };
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(filter === key ? 'all' : key)}
                    className={`p-3 rounded-lg border text-left transition hover:shadow-sm ${filter === key ? 'ring-2 ring-brand-red' : ''} ${meta.className}`}
                    data-testid={`imikel-filter-${key}`}
                  >
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-xs uppercase tracking-wide">{meta.label}</div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between border-t pt-4 flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
                <Filter className="w-4 h-4" />
                Filter: <strong>{filter === 'all' ? 'Alle' : STATUS_META[filter]?.label}</strong> ({filteredItems.length})
                {filter !== 'all' && <button className="text-xs text-brand-red hover:underline ml-1" onClick={() => setFilter('all')}>zurücksetzen</button>}
                {multiItems.length > 0 && (
                  <span className="ml-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"
                    data-testid="imikel-multi-progress">
                    Mehrdeutig: {multiPicked} / {multiItems.length} gepickt
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {multiAutoPickable.length > 0 && (
                  <Button onClick={autoPickMultis} variant="outline" size="sm"
                    className="text-amber-800 border-amber-300 hover:bg-brand-red/5"
                    data-testid="imikel-autopick-btn">
                    Auto-Pick ({multiAutoPickable.length})
                  </Button>
                )}
                <Button onClick={runCommit} disabled={committing || willCommitCount === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="imikel-commit-btn">
                  {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  {willCommitCount} Mandate übernehmen
                </Button>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto border rounded-md">
              <ListModePager
                listMode={listMode}
                setListMode={setListMode}
                pageSize={pageSize}
                setPageSize={setPageSize}
                listPage={listPage}
                setListPage={setListPage}
                totalCount={totalCount}
                pageCount={pageCount}
                visibleCount={visibleItems.length}
                label="Mandate"
                testId="imikel-list-pager"
              />
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                  <TableRow>
                    <TableHead className="w-28">Status</TableHead>
                    <TableHead>XML-Name</TableHead>
                    <TableHead className="font-mono text-xs">IBAN</TableHead>
                    <TableHead>Match in DB</TableHead>
                    <TableHead className="w-24 text-right">Betrag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Keine Einträge im aktuellen Filter</TableCell></TableRow>
                  ) : visibleItems.map(item => {
                    const meta = STATUS_META[item.status] || {};
                    return (
                      <TableRow key={item.row_index} data-testid={`imikel-row-${item.row_index}`}>
                        <TableCell>
                          <Badge className={meta.className}>{meta.label}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{item.xml_name}</TableCell>
                        <TableCell className="font-mono text-xs">{item.xml_iban?.slice(0, 4)}…{item.xml_iban?.slice(-4)}</TableCell>
                        <TableCell>
                          {item.status === 'matched' && (
                            <div className="text-sm">
                              <div className="font-medium">{item.matched_guardian_name}</div>
                              <div className="text-xs text-slate-500">{item.matched_family_kassenzeichen} · {item.matched_family_address}</div>
                            </div>
                          )}
                          {item.status === 'iban_unchanged' && (
                            <div className="text-sm text-blue-700">{item.matched_guardian_name} (IBAN bereits identisch)</div>
                          )}
                          {item.status === 'multi' && (
                            <div className="space-y-1">
                              <div className="text-xs text-amber-700 mb-1">{item.candidates.length} Kandidaten — bitte wählen:</div>
                              <div className="flex flex-wrap gap-1">
                                {item.candidates.map(c => {
                                  const isPicked = pickedCandidates[item.row_index] === c.guardian_id;
                                  const disabled = !c.guardian_id;
                                  // Recommended = exactly one candidate has guardian + no IBAN yet
                                  const eligibleNoIban = item.candidates.filter(x => x.guardian_id && !x.has_iban);
                                  const isRecommended = eligibleNoIban.length === 1 && eligibleNoIban[0].guardian_id === c.guardian_id;
                                  return (
                                    <button
                                      key={c.kassenzeichen}
                                      onClick={() => !disabled && setPickedCandidates({ ...pickedCandidates, [item.row_index]: isPicked ? null : c.guardian_id })}
                                      disabled={disabled}
                                      className={`text-xs px-2 py-1 rounded border ${isPicked
                                          ? 'bg-brand-red text-white border-brand-red'
                                          : isRecommended
                                            ? 'bg-emerald-50 border-emerald-400 hover:bg-emerald-100 text-emerald-900'
                                            : 'bg-white border-slate-300 hover:bg-slate-50'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      title={c.address}
                                      data-testid={`imikel-cand-${item.row_index}-${c.kassenzeichen}`}
                                    >
                                      {isRecommended && !isPicked && <span className="mr-1">★</span>}
                                      {c.kassenzeichen} · {c.address}{c.has_iban ? ' (IBAN vorh.)' : ''}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {item.status === 'no_payer' && (
                            <div className="text-sm text-slate-500">Familie {item.matched_family_kassenzeichen} hat keinen zahlenden Erziehungsberechtigten</div>
                          )}
                          {item.status === 'unmatched' && (
                            <div className="text-sm text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Kein Match in Excel/DB</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {item.xml_amount != null ? `${formatDeNumber(item.xml_amount)} €` : ''}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
